import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useRouteMatch } from "react-router-dom";
import { NetworkIntegrityAggregateGroup, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityViewAllByGroupResult } from "@app/projects/network-assessment/network-integrity/models/networkIntegrityViewAllByGroupResult";
import { NetworkIntegrityRouteState } from "@app/projects/network-assessment/network-integrity/models/NetworkIntegrityRouteState";
import { NetworkIntegrityProviderTypes, NetworkIntegrityDisplayMode, NetworkIntegrityMembership } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkIntegrityViewAllByGroup } from "@app/projects/network-assessment/network-integrity/models/networkIntegrityViewAllByGroup";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityFilterContainer from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityFilterContainer";
import { NetworkIntegrityChangeArrow } from "@app/projects/network-assessment/network-integrity/widgets/NetworkIntegrityChangeArrow";
import { attestationBasedAlertDisplayKeyToAggregateKey, optionExclusionDisplayKeyToAggregateKey } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityDisplayHelper";
interface NetworkIntegrityViewAllTableData {
  [wafflesKeyColumnName]: string;
  groupName: string;
  providerCount: JSX.Element;
  totalAfterTermed?: JSX.Element;
  change?: JSX.Element;
  percentChange?: JSX.Element;
  rawAfterTermedCount?: number;
  rawProviderCount?: number;
  rawChange?: number;
  rawPercentChange?: number;
}

interface RouteMatchParams {
  aggregateGroup: NetworkIntegrityAggregateGroup;
  projectId: string;
  projectRunConfigurationId: string;
}

const NetworkIntegrityViewAllPage = (): JSX.Element => {
  const routeMatch = useRouteMatch<RouteMatchParams>();
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const showTotalAfterTermed = networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.TotalAfterTermed
  const project = networkIntegrityContext.project;
  const pageSize = 100;
  const defaultPrimaryMemberAlias = "Primary Membership";
  const defaultSecondaryMemberAlias = "Secondary Membership";

  const locationState = useLocation<NetworkIntegrityRouteState>().state;
  const { aggregateGroup, projectId, projectRunConfigurationId } = routeMatch.params;

  const [loading, setLoading] = useState<boolean>(true);
  const [groupResults, setGroupResults] = useState<NetworkIntegrityViewAllByGroupResult>(null);
  const [projectRunConfiguration, setProjectRunConfiguration] = useState<ProjectRunConfiguration>(locationState?.runConfiguration);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [primaryMemberAlias, setPrimaryMemberAlias] = useState<string>(projectRunConfiguration?.memberSetAlias == defaultPrimaryMemberAlias ? "Primary" : projectRunConfiguration?.memberSetAlias);
  const [secondaryMemberAlias, setSecondaryMemberAlias] = useState<string>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (!project) return;
    const isSecondaryProject = projectRunConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental;

    if (!projectRunConfiguration || isSecondaryProject) {
      const primaryRunConfig = project.projectRunConfigurations?.find(
        (rc) => rc.projectRunConfigurationId === Number(isSecondaryProject ? projectRunConfiguration.companionProjectRunConfigurationId : projectRunConfigurationId)
      );

      if (primaryRunConfig) {
        setProjectRunConfiguration(primaryRunConfig);

        if (primaryRunConfig.companionProjectRunConfigurationId != null) {
          const companionConfig = project.projectRunConfigurations?.find(
            (rc) => rc.projectRunConfigurationId === Number(primaryRunConfig.companionProjectRunConfigurationId)
          );

          setPrimaryMemberAlias(primaryRunConfig.memberSetAlias == defaultPrimaryMemberAlias ? "Primary" : primaryRunConfig.memberSetAlias);
          if (companionConfig) {
            setSecondaryMemberAlias(companionConfig.memberSetAlias == defaultSecondaryMemberAlias ? "Secondary" : companionConfig.memberSetAlias);
          }
        }
      }

      setIsInitialized(true);
    }
    else if (projectRunConfiguration.companionProjectRunConfigurationId != null) {
      const companionConfig = project.projectRunConfigurations?.find(
        rc => rc.projectRunConfigurationId === Number(projectRunConfiguration.companionProjectRunConfigurationId)
      );
      if (companionConfig) {
        setSecondaryMemberAlias(companionConfig.memberSetAlias == defaultSecondaryMemberAlias ? "Secondary" : companionConfig.memberSetAlias);
      }
      setIsInitialized(true);
    }
    else {
      setIsInitialized(true);
    }
  }, [project, projectId, projectRunConfigurationId, projectRunConfiguration]);

  useEffect(() => {
    if (!isInitialized || networkIntegrityContext.isLoading) return;

    let cancelled = false;

    async function fetchProviders() {
      setLoading(true);
      const results = await networkIntegrityService.getViewAllByAggregateGroupResults(
        Number(projectId),
        Number(networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId),
        aggregateGroup,
        networkIntegrityContext.providerType,
        networkIntegrityContext.regionSetIds ?? [],
        networkIntegrityContext.specialtyCodes ?? []
      );
      if (!cancelled) {
        setGroupResults(results);
        setLoading(false);
      }
    };
    fetchProviders();

    return () => { cancelled = true; };
  }, [isInitialized, networkIntegrityContext.isLoading,
      networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, networkIntegrityContext.providerType,
      networkIntegrityContext.projectRunConfigurationId,
      projectId, projectRunConfigurationId, aggregateGroup]);

  const getTitle = (): string => {
    let title = ""
    switch (aggregateGroup) {
      case NetworkIntegrityAggregateGroup.ProvidersByCounty:
        title += "Providers By County";
        break;

      case NetworkIntegrityAggregateGroup.ProvidersByGeocode:
        title += "Providers By Geocode";
        break;

      case NetworkIntegrityAggregateGroup.ProvidersBySpecialty:
        title += "Providers By Specialty";
        break;

      case NetworkIntegrityAggregateGroup.ProvidersByState:
        title += "Providers By State";
        break;

      case NetworkIntegrityAggregateGroup.ProvidersByZipCode:
        title += "Providers By Zip Code";
        break;

      case NetworkIntegrityAggregateGroup.ProvidersByZipClass:
        title += "Providers By Zip Code Classification";
        break;

      case NetworkIntegrityAggregateGroup.CriticalAlerts:
        title += "Critical Alerts";
        break;

      case NetworkIntegrityAggregateGroup.AttestationBasedAlerts:
        title += "Attestation-based Alerts";
        break;

      case NetworkIntegrityAggregateGroup.OptionalExclusions:
        title += "Project Option Exclusions";
        break;

    };

    return title
  };

  const getFirstColumnName = (): string => {
    let columnName = ""
    switch (aggregateGroup) {
      case NetworkIntegrityAggregateGroup.ProvidersByCounty:
        columnName = "County";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByGeocode:
        columnName = "Geocode";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersBySpecialty:
        columnName = "Specialty";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByState:
        columnName = "State";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByZipCode:
        columnName = "Zip Code";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByZipClass:
        columnName = "Zip Code Classification";
        break;
      case NetworkIntegrityAggregateGroup.CriticalAlerts:
      case NetworkIntegrityAggregateGroup.AttestationBasedAlerts:
        columnName = "Alert"
        break;
      case NetworkIntegrityAggregateGroup.OptionalExclusions:
        columnName = "Exclusion"
        break;
    };
    return columnName
  }

  const getAggregateGroupDisplayName = (): string => {
    let columnName = ""
    switch (aggregateGroup) {
      case NetworkIntegrityAggregateGroup.ProvidersByCounty:
        columnName = "counties";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByGeocode:
        columnName = "geocodes";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersBySpecialty:
        columnName = "specialties";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByState:
        columnName = "states";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByZipCode:
        columnName = "zip codes";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByZipClass:
        columnName = "zip classes";
        break;
      case NetworkIntegrityAggregateGroup.CriticalAlerts:
      case NetworkIntegrityAggregateGroup.AttestationBasedAlerts:
        columnName = "alerts"
        break;
      case NetworkIntegrityAggregateGroup.OptionalExclusions:
        columnName = "exclusions"
        break;
    };
    return columnName
  }

  const parseAggregateKey = (aggregateKey: string) => {
    const keyParts = aggregateKey.split('|');
    const isUnknown = keyParts[0]?.toLowerCase() === 'unknown';

    return { isUnknown, keyParts };
  }

  const getFirstColumnValue = (groupKey: string): string => {
    let value = "";
    const { isUnknown, keyParts } = parseAggregateKey(groupKey);
    switch (aggregateGroup) {
      case NetworkIntegrityAggregateGroup.ProvidersByCounty:
        value = isUnknown ? `Unknown: ${keyParts[1]}` : keyParts[1];
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByGeocode:
      case NetworkIntegrityAggregateGroup.ProvidersByZipCode:
      case NetworkIntegrityAggregateGroup.ProvidersByZipClass:
      case NetworkIntegrityAggregateGroup.CriticalAlerts:
        value = groupKey;
        break;
      case NetworkIntegrityAggregateGroup.ProvidersBySpecialty:
        value = isUnknown ? `Unknown: ${keyParts[1]}` : `${keyParts[0]} - ${keyParts[1]}`;
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByState:
       value = isUnknown ? `Unknown: ${keyParts[1]}` : keyParts[1];
        break;
      case NetworkIntegrityAggregateGroup.AttestationBasedAlerts:
        value = Array.from(attestationBasedAlertDisplayKeyToAggregateKey.entries()).find((entry) => entry[1] === groupKey)?.[0];
        break;
      case NetworkIntegrityAggregateGroup.OptionalExclusions:
        value = Array.from(optionExclusionDisplayKeyToAggregateKey.entries()).find((entry) => entry[1] === groupKey)?.[0];
        break;
    }

    return value;
  }

  const createLinkData = (groupKey: string, linkValue: number): JSX.Element => {
    if (linkValue == 0) return (<span>0</span>);

    const providerType = networkIntegrityContext.providerType ?? NetworkIntegrityProviderTypes.Network;
    const runConfigId = networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId;

    return <Link
              to={{
                pathname: `/projects/${projectId}/${runConfigId}/network-integrity/providers/${providerType}/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(groupKey)}`,
                state: {
                  initialRegionSetIds: networkIntegrityContext.regionSetIds,
                  initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
                  runConfiguration: projectRunConfiguration
                }
              }}>
                {linkValue.toLocaleString()}
            </Link>;
  };

  const getProviderColumnLabel = (): string => {
    const providerType = networkIntegrityContext.providerType ?? NetworkIntegrityProviderTypes.Network;
    const membershipAlias = networkIntegrityContext.membership === NetworkIntegrityMembership.Secondary
      ? secondaryMemberAlias
      : primaryMemberAlias;
    const isDualMembership = secondaryMemberAlias != null;
    switch (providerType) {
      case NetworkIntegrityProviderTypes.Network:
        return "Network";
      case NetworkIntegrityProviderTypes.Servicing:
        return isDualMembership ? `Servicing (${membershipAlias})` : "Servicing";
      case NetworkIntegrityProviderTypes.Prospect:
        return "Prospect";
      case NetworkIntegrityProviderTypes.ProspectServicing:
        return isDualMembership ? `Servicing Prospect (${membershipAlias})` : "Servicing Prospect";
      default:
        return "";
    }
  };

  const buildTableHeader = (): ColumnConfig => {
    if (showTotalAfterTermed) {
      return {
        change: { displayText: "Change", sizeContentToFit: true, testId: "view-all-change" },
        groupName: { displayText: getFirstColumnName(), sizeContentToFit: true, testId: "view-all-group-name" },
        percentChange: { displayText: "% Change", sizeContentToFit: true, testId: "view-all-percent-change" },
        providerCount: { displayText: "Current", sizeContentToFit: true, testId: "view-all-count" },
        totalAfterTermed: { displayText: "Total After Termed", sizeContentToFit: true, testId: "view-all-total-after-termed" },
      };
    }
    return {
      groupName: {
        displayText: getFirstColumnName(),
        sizeContentToFit: true,
        testId: "view-all-group-name",
      },
      providerCount: {
        displayText: getProviderColumnLabel(),
        sizeContentToFit: true,
        testId: "view-all-count",
      },
    };
  }

  const renderChangeCell = (change: number): JSX.Element => {
    if (change < 0) {
      return (
        <span className="ni-table__decrease-indicator">
          {change.toLocaleString()}
          <NetworkIntegrityChangeArrow direction="decrease" />
        </span>
      );
    }
    return (
      <span className="ni-table__no-change-indicator">
        0{" "}
        <NetworkIntegrityChangeArrow direction="no-change" />
      </span>
    );
  };

  const renderPercentChangeCell = (change: number, percentChange: number): JSX.Element => {
    if (change < 0) {
      return (
        <span className="ni-table__decrease-indicator">
          {percentChange.toFixed(1)}%
          <NetworkIntegrityChangeArrow direction="decrease" />
        </span>
      );
    }
    return (
      <span className="ni-table__no-change-indicator">
        0%{" "}
        <NetworkIntegrityChangeArrow direction="no-change" />
      </span>
    );
  };

  /* oxlint-disable sort-keys */
  const mapGroupToTableData = (group: NetworkIntegrityViewAllByGroup): NetworkIntegrityViewAllTableData => {
    const current = group.providerCount;

    if (!showTotalAfterTermed) {
      return {
        [wafflesKeyColumnName]: group.key,
        groupName: getFirstColumnValue(group.key),
        providerCount: createLinkData(group.key, current),
        rawAfterTermedCount: group.afterTermedCount,
        rawProviderCount: current,
      };
    }

    const afterTermed = group.afterTermedCount ?? current;
    const change = afterTermed - current;
    const percentChange = current !== 0 ? (change / current) * 100 : 0;

    return {
      [wafflesKeyColumnName]: group.key,
      groupName: getFirstColumnValue(group.key),
      providerCount: createLinkData(group.key, current),
      totalAfterTermed: createLinkData(group.key, afterTermed),
      change: renderChangeCell(change),
      percentChange: renderPercentChangeCell(change, percentChange),
      rawAfterTermedCount: group.afterTermedCount,
      rawProviderCount: current,
      rawChange: change,
      rawPercentChange: percentChange,
    };
  }

  const sortTableData = (a: NetworkIntegrityViewAllTableData, b: NetworkIntegrityViewAllTableData) => {
    // sort unknowns to the bottom
    const aIsUnknown = a[wafflesKeyColumnName].startsWith("Unknown");
    const bIsUnknown = b[wafflesKeyColumnName].startsWith("Unknown");

    if (aIsUnknown && !bIsUnknown) return 1;
    if (!aIsUnknown && bIsUnknown) return -1;
    if (aIsUnknown && bIsUnknown) {
      if (a.groupName < b.groupName) return -1;
      if (a.groupName > b.groupName) return 1;
    }

    switch (aggregateGroup) {
      case NetworkIntegrityAggregateGroup.ProvidersByCounty:
        const countySsaA = a[wafflesKeyColumnName].split('|')[0];
        const countySsaB = b[wafflesKeyColumnName].split('|')[0];
        if (countySsaA < countySsaB) return -1;
        if (countySsaA > countySsaB) return 1;
        return 0;
    }

    if (a.groupName < b.groupName) return -1;
    if (a.groupName > b.groupName) return 1;
    return 0;
  }

  const customSort = (segment: NetworkIntegrityViewAllTableData[], column: keyof NetworkIntegrityViewAllTableData, direction: SortDirections) => {
    if(direction === SortDirections.None)
      return segment;
    const data = groupResults.groups.map(mapGroupToTableData);

    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    const startIndex = currentPage * pageSize

    const byNumber = (selector: (row: NetworkIntegrityViewAllTableData) => number | undefined) =>
      data.sort((a, b) => ((selector(a) ?? 0) - (selector(b) ?? 0)) * multiplier).slice(startIndex, startIndex + pageSize);

    switch (column) {
      case "providerCount":
        return showTotalAfterTermed
          ? byNumber(r => r.rawProviderCount)
          : byNumber(r => r.rawAfterTermedCount ?? r.rawProviderCount);
      case "totalAfterTermed":
        return byNumber(r => r.rawAfterTermedCount ?? r.rawProviderCount);
      case "change":
        return byNumber(r => r.rawChange);
      case "percentChange":
        return byNumber(r => r.rawPercentChange);
      default:
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier).slice(startIndex, startIndex + pageSize);
    }
  };

  const renderTable = () => {
    let tableDataProvider = groupResults.groups.map(mapGroupToTableData);

    // Implement default sort based on business requirements
    tableDataProvider.sort(sortTableData);
    const startIndex = currentPage * pageSize
    tableDataProvider = tableDataProvider.slice(startIndex, startIndex + pageSize)

    const totalCount = groupResults.totalGroupCount;

    return (
      <div className="row">
        <div className="col-12">
          {!loading && (
            <p className="mb5">Displaying {tableDataProvider.length.toLocaleString()} of {totalCount.toLocaleString()} total {getAggregateGroupDisplayName()}</p>
          )}
          <Waffles<NetworkIntegrityViewAllTableData>
            columns={buildTableHeader()}
            data={tableDataProvider}
            testId={`view-all-table`}
            showCheckboxes={false}
            loading={loading}
            totalNumberOfRecords={totalCount}
            enablePagination={true}
            paginationProps={{
                        changePage: (pageNumber: number) => {setCurrentPage(pageNumber-1)},
                        recordsPerPage: pageSize,
                        skip: currentPage*pageSize,
                        totalRecords: totalCount
                    }}
            customSort={customSort}
          />
        </div>
      </div>
    );
  }
  /* oxlint-enable sort-keys */

  const title = getTitle();

  return <Page documentTitle={`Network Integrity ${title}`}>
    <PageInner>
      <PageHeader title={title}>
        <PageHeaderRight>
          <Link
            className="qes-button qes-button-secondary"
            type="button"
            id="network-integrity-providers__back"
            to={{
              pathname: `/projects/${projectId}/network-integrity`,
              state: {
                initialProviderType: locationState?.initialProviderType,
                initialRegionSetIds: networkIntegrityContext.regionSetIds,
                initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
                runConfiguration: projectRunConfiguration
              }
            }}
          >
            Return to Project
          </Link>
        </PageHeaderRight>
      </PageHeader>
        <PageSection>
          <NetworkIntegrityFilterContainer isViewAllPage />
        </PageSection>
      {loading && (
        <PageSection>
          <LoadingSpinner />
        </PageSection>
      )}
      {!loading && (
        <PageSection>
          {renderTable()}
        </PageSection>
      )}
    </PageInner>
  </Page>
};

export { NetworkIntegrityViewAllPage };